/**
 * FiveM / GTA V coordinate helper — pure, dependency-free, unit-testable.
 *
 * FiveM scripts consume positions in a handful of interchangeable shapes:
 * Lua `vector3(x, y, z)` / `vec3(...)`, C#/JS `Vector3`, a `vector4` with a
 * heading, a Lua table `{ x = , y = , z = }`, or a plain CSV row. Players copy
 * coordinates out of trainers, `/coords` commands, F8 console output, or map
 * tools in wildly inconsistent formats. `parseCoords` normalizes any of those
 * into numbers; `formatCoords` renders them into whichever shape a script needs.
 */

export interface ParsedCoords {
  x: number;
  y: number;
  z: number;
  /** Optional 4th component — heading (`w`) when a vector4 is supplied. */
  heading?: number;
}

export type CoordFormat =
  | "vector3"
  | "vec3"
  | "vector4"
  | "csharpVector3"
  | "luaTable"
  | "jsObject"
  | "array"
  | "csv";

/** Human-facing label for each output format (used to build the UI toggles). */
export const COORD_FORMAT_LABELS: Record<CoordFormat, string> = {
  vector3: "Lua vector3",
  vec3: "Lua vec3",
  vector4: "vector4 (+ heading)",
  csharpVector3: "C# new Vector3",
  luaTable: "Lua table",
  jsObject: "JS object",
  array: "Array",
  csv: "Plain CSV",
};

const NUMBER_RE = /-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?/g;

// `vector3(` / `vec4` / `new Vector3(` glue a digit to the keyword; strip the
// whole wrapper token (keyword + its digit) so we never mistake the "3" in
// "vector3" for a coordinate. Lookbehind is avoided for older-browser safety.
const WRAPPER_RE = /\b(?:vector|vec)\s*[0-9]+/gi;

/**
 * Extract coordinates from an arbitrary pasted string. Pulls every number out of
 * the input (ignoring labels like `X:`, brackets, `vector3(...)`, commas) and
 * uses the first three as x/y/z, with an optional fourth as heading.
 *
 * Returns `null` when fewer than three numbers are present.
 */
export function parseCoords(input: string): ParsedCoords | null {
  if (!input) return null;
  const matches = input.replace(WRAPPER_RE, " ").match(NUMBER_RE);
  if (!matches || matches.length < 3) return null;

  const nums = matches.map(Number);
  if (nums.slice(0, 3).some((n) => !Number.isFinite(n))) return null;

  const result: ParsedCoords = { x: nums[0], y: nums[1], z: nums[2] };
  if (matches.length >= 4 && Number.isFinite(nums[3])) {
    result.heading = nums[3];
  }
  return result;
}

function round(n: number, precision: number): string {
  if (!Number.isFinite(n)) return "0";
  // Trim trailing zeros but keep the value readable (e.g. 12.5 not 12.50000).
  const fixed = n.toFixed(precision);
  return fixed.replace(/\.?0+$/, "") || "0";
}

/** Render parsed coordinates into the requested format. */
export function formatCoords(
  coords: ParsedCoords,
  format: CoordFormat,
  precision = 2,
): string {
  const x = round(coords.x, precision);
  const y = round(coords.y, precision);
  const z = round(coords.z, precision);
  const h = round(coords.heading ?? 0, precision);

  switch (format) {
    case "vector3":
      return `vector3(${x}, ${y}, ${z})`;
    case "vec3":
      return `vec3(${x}, ${y}, ${z})`;
    case "vector4":
      return `vector4(${x}, ${y}, ${z}, ${h})`;
    case "csharpVector3":
      return `new Vector3(${x}f, ${y}f, ${z}f)`;
    case "luaTable":
      return `{ x = ${x}, y = ${y}, z = ${z} }`;
    case "jsObject":
      return `{ x: ${x}, y: ${y}, z: ${z} }`;
    case "array":
      return `[${x}, ${y}, ${z}]`;
    case "csv":
      return `${x}, ${y}, ${z}`;
    default:
      return `vector3(${x}, ${y}, ${z})`;
  }
}

/** Every format rendered at once — handy for the copy-all view. */
export function formatAll(
  coords: ParsedCoords,
  precision = 2,
): { format: CoordFormat; label: string; value: string }[] {
  return (Object.keys(COORD_FORMAT_LABELS) as CoordFormat[]).map((format) => ({
    format,
    label: COORD_FORMAT_LABELS[format],
    value: formatCoords(coords, format, precision),
  }));
}
